import { Navigate, useLocation, type Location } from 'react-router';
import type { ReactNode } from 'react';
import { useSession } from './session';
import { ErrorState, Spinner } from '../components/ui';

/**
 * The two route guards.
 *
 * In their own module rather than in `main.tsx` because that file mounts the
 * application when it is imported, which makes it untestable — and these are
 * precisely the pieces worth testing. Between them they decide whether anybody
 * gets in at all.
 */

/**
 * A path inside this application, or null.
 *
 * The "come back to" address arrives in router state or, from the SSO round
 * trip, in a query parameter anybody can write. Only a path on this origin is
 * honoured: `//evil.example` is a protocol-relative URL to somewhere else, and
 * a backslash is read as a slash by some browsers.
 */
export function safeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 1024) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  return raw;
}

/**
 * Where to go once signed in: the page they were refused, if they were sent
 * from one, then a `redirect` parameter, then home.
 */
export function returnPathFrom(location: Pick<Location, 'state' | 'search'>): string {
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const fromQuery = new URLSearchParams(location.search).get('redirect');
  return safeReturnPath(fromState) ?? safeReturnPath(fromQuery) ?? '/';
}

function Checking({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner label={label} />
    </div>
  );
}

/**
 * Requires a session.
 *
 * The attempted path is carried through the redirect so signing in returns the
 * user where they were going, rather than dumping them on the dashboard — which
 * matters most for the deep links people paste to each other, and for a
 * session that lapsed while somebody was in the middle of something. A
 * deliberate end (signing out, changing a password) goes where it asked to,
 * without a way back.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { session, isLoading, isFetching, error, refetch, sessionEndedTo } = useSession();
  const location = useLocation();

  // `isFetching` as well as `isLoading`: with nobody signed in, a check in
  // flight — the one signing in starts, say — is "we are asking", and sending
  // the person to the sign-in page meanwhile would bounce them straight back
  // to the screen they had just left.
  if (isLoading || (isFetching && !session)) return <Checking label="Checking your session" />;

  if (!session) {
    // A network failure is not a signed-out state; sending the user to the
    // login screen would be a lie, and they would sign in to no effect.
    if (error && !error.isAuthError) {
      return (
        <ErrorState
          title="Cannot reach the service desk"
          message={error.message}
          requestId={error.requestId}
          onRetry={refetch}
        />
      );
    }
    if (sessionEndedTo) return <Navigate to={sessionEndedTo} replace />;
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { session, isLoading } = useSession();
  const location = useLocation();

  /**
   * This is the one place a sign-in moves somebody on: the moment the session
   * reaches the provider, to the page they were going to.
   *
   * The sign-in handlers used to navigate as well, and always got there first
   * — React Query tells the provider on a timer, a tick after the fetch
   * resolves — so the destination opened while its own guard still saw
   * nobody, bounced them back here, and opened again. The handlers now only
   * load the session (`openSession`) and leave the moving to this.
   */
  if (session) return <Navigate to={returnPathFrom(location)} replace />;

  /**
   * Only the first check holds the form back.
   *
   * Once we know nobody is signed in, a background check — the window
   * regaining focus — must leave the form alone. Swapping it for a spinner
   * unmounted it, and took with it whatever the person had typed.
   */
  if (isLoading) return <Checking label="Checking your session" />;

  return <>{children}</>;
}
